import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  BarChart3,
  Coins,
  Loader2,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";
import {
  referralCohort,
  referralSummary,
  referralTimeseries,
  topReferrers,
} from "@/lib/referralAnalytics";

const RANGES = [
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "all", label: "All time" },
];

function fmtInr(v) {
  return `₹${Number(v || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
}
function fmtUsd(v) {
  return `$${Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function fmtPct(v) {
  return `${(Number(v || 0) * 100).toFixed(1)}%`;
}

function KpiCard({ label, value, sublabel, testid, tone = "stone", icon: Icon }) {
  const toneMap = {
    stone: "border-stone-200 bg-white",
    amber: "border-amber-200 bg-amber-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
    purple: "border-purple-200 bg-purple-50/40",
  };
  return (
    <Card className={toneMap[tone]} data-testid={testid}>
      <CardContent className="pt-4 pb-4 space-y-1">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-stone-500">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
        </div>
        <div className="text-2xl font-semibold text-stone-900">{value}</div>
        {sublabel && <div className="text-xs text-stone-500">{sublabel}</div>}
      </CardContent>
    </Card>
  );
}

export default function AdminReferrals() {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [top, setTop] = useState([]);
  const [cohort, setCohort] = useState([]);
  const [ts, setTs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("cvr");

  useEffect(() => {
    let stopped = false;
    setLoading(true);
    const tsRange = range === "all" ? "90d" : range;
    Promise.all([
      referralSummary(),
      topReferrers({ range, limit: 10 }),
      referralCohort({ range }),
      referralTimeseries({ range: tsRange, bucket: range === "90d" ? "week" : "day" }),
    ])
      .then(([s, t, c, ts]) => {
        if (stopped) return;
        setSummary(s);
        setTop(t);
        setCohort(c);
        setTs(ts);
      })
      .catch(() => {})
      .finally(() => !stopped && setLoading(false));
    return () => {
      stopped = true;
    };
  }, [range]);

  const sortedCohort = useMemo(() => {
    const rows = [...cohort];
    rows.sort((a, b) => {
      const va = Number(a[sortKey] || 0);
      const vb = Number(b[sortKey] || 0);
      return vb - va;
    });
    return rows;
  }, [cohort, sortKey]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <DashboardHeader title="Admin" subtitle="Overview" />
      <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <BackButton to="/admin" label="Back to admin" />
            <h1 className="text-2xl font-semibold mt-2 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-600" /> Referral funnel
            </h1>
            <p className="text-sm text-stone-500">
              Track how the referral flywheel is turning: created → converted →
              credit redeemed → revenue attributed.
            </p>
          </div>
          <div className="flex items-center gap-1" data-testid="range-picker">
            {RANGES.map((r) => (
              <Button
                key={r.key}
                variant={range === r.key ? "default" : "outline"}
                size="sm"
                onClick={() => setRange(r.key)}
                data-testid={`range-${r.key}`}
                className={
                  range === r.key
                    ? "bg-stone-900 hover:bg-stone-800 text-white"
                    : ""
                }
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>

        {loading || !summary ? (
          <div className="py-12 text-center text-stone-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard
                label="Referrals created"
                testid="kpi-created"
                icon={Users}
                value={summary.referrals_created}
                sublabel={`${summary.referrals_active} active · ${summary.referrals_disabled} disabled`}
              />
              <KpiCard
                label="Conversions"
                testid="kpi-conversions"
                tone="emerald"
                icon={Target}
                value={summary.successful_conversions}
                sublabel={`${fmtPct(summary.conversion_rate)} conversion rate`}
              />
              <KpiCard
                label="Credits redeemed"
                testid="kpi-credits"
                tone="amber"
                icon={Coins}
                value={
                  <>
                    {fmtInr(summary.credits_redeemed_inr)}
                    {summary.credits_redeemed_usd > 0 && (
                      <span className="text-base text-stone-500 ml-2">
                        · {fmtUsd(summary.credits_redeemed_usd)}
                      </span>
                    )}
                  </>
                }
                sublabel={`${fmtInr(summary.credits_granted_inr)} granted (${fmtPct(
                  summary.redemption_rate
                )} redeemed)`}
              />
              <KpiCard
                label="Attributed revenue"
                testid="kpi-revenue"
                tone="purple"
                icon={ShieldCheck}
                value={
                  <>
                    {fmtInr(summary.attributed_revenue_inr)}
                    {summary.attributed_revenue_usd > 0 && (
                      <span className="text-base text-stone-500 ml-2">
                        · {fmtUsd(summary.attributed_revenue_usd)}
                      </span>
                    )}
                  </>
                }
                sublabel="Sum of paid installments on referred enrollments"
              />
            </div>

            <Card data-testid="timeseries-card">
              <CardHeader className="pb-1">
                <CardTitle className="text-sm">
                  Daily activity
                  <span className="ml-2 text-xs font-normal text-stone-500">
                    {range === "90d"
                      ? "Weekly buckets over 90 days"
                      : "Daily buckets over the selected range"}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="h-64" data-testid="timeseries-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ts} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 10 }}
                        allowDecimals={false}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="referrals_created"
                        stroke="#d97706"
                        strokeWidth={2}
                        dot={false}
                        name="Created"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="conversions"
                        stroke="#059669"
                        strokeWidth={2}
                        dot={false}
                        name="Conversions"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="credits_redeemed_inr"
                        stroke="#9333ea"
                        strokeWidth={2}
                        dot={false}
                        name="Credits ₹"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="top-referrers-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4 text-amber-600" />
                  Top referrers
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-stone-500">
                    <tr>
                      <th className="text-left py-2 px-2">Student</th>
                      <th className="text-left py-2 px-2">Email</th>
                      <th className="text-right py-2 px-2">Referrals</th>
                      <th className="text-right py-2 px-2">Conversions</th>
                      <th className="text-right py-2 px-2">Credits earned</th>
                      <th className="text-right py-2 px-2">Revenue attrib.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="py-6 text-center text-stone-500 text-sm"
                          data-testid="top-referrers-empty"
                        >
                          No referrers in this range yet.
                        </td>
                      </tr>
                    ) : (
                      top.map((r) => (
                        <tr
                          key={r.student_id}
                          className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer"
                          onClick={() =>
                            navigate(
                              `/admin/enrollments?student_id=${r.student_id}`
                            )
                          }
                          data-testid={`top-referrer-row-${r.student_id}`}
                        >
                          <td className="py-2 px-2 font-medium">
                            {r.student_name}
                          </td>
                          <td className="py-2 px-2 text-stone-600">
                            {r.student_email}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {r.referrals_created}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <Badge
                              variant="outline"
                              className={
                                r.successful_conversions > 0
                                  ? "border-emerald-300 text-emerald-800 bg-emerald-50"
                                  : "border-stone-200 text-stone-500"
                              }
                            >
                              {r.successful_conversions}
                            </Badge>
                          </td>
                          <td className="py-2 px-2 text-right">
                            {fmtInr(r.credits_earned_inr)}
                            {r.credits_earned_usd > 0 && (
                              <span className="text-stone-500 text-xs ml-1">
                                / {fmtUsd(r.credits_earned_usd)}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {fmtInr(r.attributed_revenue_inr)}
                            {r.attributed_revenue_usd > 0 && (
                              <span className="text-stone-500 text-xs ml-1">
                                / {fmtUsd(r.attributed_revenue_usd)}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card data-testid="cohort-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-600" />
                  Course cohort
                </CardTitle>
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-stone-500">Sort by:</span>
                  {[
                    ["cvr", "CVR"],
                    ["successful_conversions", "Conv."],
                    ["roi", "ROI"],
                    ["attributed_revenue_inr", "Revenue"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setSortKey(k)}
                      data-testid={`cohort-sort-${k}`}
                      className={`px-2 py-0.5 rounded ${
                        sortKey === k
                          ? "bg-stone-900 text-white"
                          : "text-stone-600 hover:bg-stone-100"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-stone-500">
                    <tr>
                      <th className="text-left py-2 px-2">Course</th>
                      <th className="text-left py-2 px-2">Teacher</th>
                      <th className="text-right py-2 px-2">Referrals</th>
                      <th className="text-right py-2 px-2">Conv.</th>
                      <th className="text-right py-2 px-2">CVR</th>
                      <th className="text-right py-2 px-2">Credits out</th>
                      <th className="text-right py-2 px-2">Revenue</th>
                      <th className="text-right py-2 px-2">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCohort.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="py-6 text-center text-stone-500 text-sm"
                          data-testid="cohort-empty"
                        >
                          No cohort data in this range yet.
                        </td>
                      </tr>
                    ) : (
                      sortedCohort.map((c) => (
                        <tr
                          key={c.course_id}
                          className="border-t border-stone-100"
                          data-testid={`cohort-row-${c.course_id}`}
                        >
                          <td className="py-2 px-2 font-medium">
                            {c.course_title}
                          </td>
                          <td className="py-2 px-2 text-stone-600">
                            {c.teacher_name}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {c.referrals_created}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {c.successful_conversions}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {fmtPct(c.cvr)}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {fmtInr(c.credits_out_inr)}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {fmtInr(c.attributed_revenue_inr)}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <span
                              className={
                                c.roi > 0
                                  ? "text-emerald-700 font-medium"
                                  : "text-stone-500"
                              }
                            >
                              {c.roi > 0 ? "+" : ""}
                              {fmtPct(c.roi)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
